<template>
  <el-drawer
    :title="$t('linePushMessageLog.detail.title')"
    :visible="show"
    size="600px"
    :append-to-body="true"
    destroy-on-close
    @close="onClose"
  >
    <div class="px-[24px]">
      <BlockSkeleton :loading="!loaded" width="100%" height="600px">
        <BobDisplayForm
          :config="{
            labelWidth: '128px',
            rowGap: 12,
          }"
          :fields="displayFields"
        />
      </BlockSkeleton>
    </div>
  </el-drawer>
</template>

<script>
import { defineComponent, ref, computed, onBeforeMount } from 'vue'
import { useRouter } from 'vue-router/composables'
import { FindLinePushMessageLog } from '@/api/linePushMessageLog'
import { linePushMessageStatusConfig } from '@/config/linePushMessageLog'
import BlockSkeleton from '@/components/BlockSkeleton.vue'
import BobDisplayForm from '@/components/BobDisplayForm.vue'
import { get, filter } from 'lodash'
import { t } from '@/plugins/i18n/i18n'
import { useShop } from '@/use/shop'
import { formatDate } from '@/utils/date'

export default defineComponent({
  name: 'LinePushMessageLogDetailDrawer',
  components: {
    BlockSkeleton,
    BobDisplayForm,
  },
  props: {
    logId: {
      type: String,
      required: true,
    },
    eventLabelMap: {
      type: Object,
      default: () => ({}),
    },
    eventData: {
      type: Array,
      default: () => [],
    },
  },
  setup (props, { emit }) {
    const router = useRouter()
    const { shopId } = useShop()
    const show = ref(false)
    const loaded = ref(false)
    const detail = ref(null)

    // 根據 event key 取得顯示名稱
    const getEventLabel = (eventKey) => {
      return props.eventLabelMap[eventKey] || eventKey
    }

    // 根據 event key 取得通知類型
    const getEventType = (eventKey) => {
      // 從 eventData 中找到對應的事件,取得其 typeLabel
      const eventItem = props.eventData.find(item =>
        item.keys.includes(eventKey),
      )
      return eventItem?.typeLabel || '-'
    }

    // 顯示欄位配置
    const displayFields = computed(() => {
      if (!detail.value) return []

      const omitFields = []

      const fields = [
        // 發送時間
        {
          prop: 'sentAt',
          label: t('linePushMessageLog.detail.field.sentAt'),
          body: {
            component: 'div',
            render: () => detail.value.sentAt
              ? formatDate(detail.value.sentAt, 'YYYY/MM/DD HH:mm:ss')
              : '-',
          },
        },
        // 會員姓名
        {
          prop: 'memberName',
          label: t('linePushMessageLog.detail.field.memberName'),
          body: {
            component: () => import('@/components/BaseElButton.vue'),
            render: () => get(detail.value, 'Member.UserInfo.name') || '-',
            props: {
              type: 'text',
              class: ['underline', '!p-0', '!text-primary-100'],
            },
            on: {
              click: () => {
                // 跳轉到會員資訊頁
                const memberId = get(detail.value, 'Member.id')
                if (memberId) {
                  router.push({
                    name: 'MemberProfile',
                    params: { id: memberId },
                    query: { tab: 'info' },
                  })
                }
              },
            },
          },
        },
        // 電話號碼
        {
          prop: 'phone',
          label: t('linePushMessageLog.detail.field.phone'),
          body: {
            component: 'div',
            render: () => get(detail.value, 'Member.UserInfo.phone') || '-',
          },
        },
        // 通知類型
        {
          prop: 'eventType',
          label: t('linePushMessageLog.detail.field.eventType'),
          body: {
            component: 'div',
            render: () => getEventType(detail.value.event),
          },
        },
        // 通知事件
        {
          prop: 'event',
          label: t('linePushMessageLog.detail.field.event'),
          body: {
            component: 'div',
            render: () => getEventLabel(detail.value.event),
          },
        },
        // 通知標題
        {
          prop: 'title',
          label: t('linePushMessageLog.detail.field.title'),
          body: {
            component: 'div',
            render: () => detail.value.title || '-',
          },
        },
        // 發送狀態
        {
          prop: 'status',
          label: t('linePushMessageLog.detail.field.status'),
          body: {
            component: () => import('@/components/Tag/Tag.vue'),
            render: () => t(`linePushMessageLog.status.${detail.value.status}`) || detail.value.status,
            props: {
              type: get(linePushMessageStatusConfig, `${detail.value.status}.tagType`) || 'info',
            },
          },
        },
        // 發送訊息數，CNCY-8886 需求暫時不顯示
        // {
        //   prop: 'content.lineMessages',
        //   label: '發送訊息數',
        //   tip: '每 5 則訊息會花費 1 則 LINE 通知額度',
        //   body: {
        //     render: () => {
        //       const messages = get(detail.value, 'content.lineMessages') || []
        //       return messages.length
        //     },
        //   },
        // },
        // 通知內容 (條件顯示: 有 content 時)
        {
          prop: 'content',
          label: t('linePushMessageLog.detail.field.content'),
          body: {
            component: 'div',
            render: () => {
              const texts = detail.value.content?.displayTexts || []
              if (texts.length === 0) return '-'
              if (texts.length === 1) return texts[0] || t('linePushMessageLog.detail.imageMessage')
              // 多則訊息換行顯示
              return texts.map(text => text || t('linePushMessageLog.detail.imageMessage')).join('\n')
            },
            props: {
              class: ['whitespace-pre-line'],
            },
          },
        },
        // LINE 用戶名稱
        {
          prop: 'lineDisplayName',
          label: t('linePushMessageLog.detail.field.lineDisplayName'),
          body: {
            component: 'div',
            render: () => get(detail.value, 'memberAuthLineSnapshot.displayName') || '-',
          },
        },
        // LINE 用戶識別
        {
          prop: 'lineUserId',
          label: t('linePushMessageLog.detail.field.lineUserId'),
          body: {
            component: 'div',
            render: () => get(detail.value, 'memberAuthLineSnapshot.lineUserId') || '-',
          },
        },
        // 錯誤訊息 (條件顯示: 發送失敗時)
        {
          prop: 'errorMessage',
          label: t('linePushMessageLog.detail.field.errorMessage'),
          body: {
            component: 'div',
            render: () => detail.value.errorMessage || '-',
          },
        },
      ]

      // 條件排除欄位
      if (!detail.value.content?.displayTexts?.length) {
        omitFields.push('content')
      }

      if (detail.value.status !== 'failed') {
        omitFields.push('errorMessage')
      }

      return filter(fields, (field) => {
        return !omitFields.includes(field.prop)
      })
    })

    // 載入詳情資料
    const loadDetail = async () => {
      loaded.value = false
      try {
        const [res, err] = await FindLinePushMessageLog({
          shopId: shopId.value,
          id: props.logId,
        })

        if (err) throw err
        detail.value = res
      } catch (error) {
        window.$message.error(error)
      } finally {
        loaded.value = true
      }
    }

    // 關閉 Drawer
    const onClose = () => {
      show.value = false
      setTimeout(() => {
        emit('close')
      }, 300)
    }

    onBeforeMount(async () => {
      show.value = true
      await loadDetail()
    })

    return {
      show,
      loaded,
      detail,
      displayFields,
      onClose,
    }
  },
})
</script>

<style lang="postcss" scoped>
</style>
